import { useCallback, useRef, useState } from 'react';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';

import Feedback from '@/components/ui/trainers/shared/Feedback';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import ImageCarousel from '@/components/ui/trainers/ImageCarousel';
import Markdown from '@/components/ui/trainers/shared/Markdown';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import ErrorMessage from './shared/ErrorMessage';

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
  modifyPosition?: boolean;
  modifyAzimuth?: boolean;
}

const HeightRestrictionType: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description: initialDescription,
  location,
  tool,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { incrementReviews } = useTrainerReviews();
  const {
    leaseId,
    landmarksWithAssets = { landmarks: [], landmarkToAssets: {} },
    elementDescription = '',
  } = data?.content || {};

  const description =
    initialDescription ||
    (data?.content
      ? `Identify the type of the structure for which the height restriction sign is installed.`
      : '');

  const isDataReady = Boolean(data?.content);

  const [isBenchmark, setIsBenchmark] = useState(false);

  const { feedback, setFeedback } = useTrainerFeedback();

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };
  const onSubmit = async ({ outcome, value }: ITrainerSubmit) => {
    clearAllData();
    if (!leaseId) {
      return;
    }
    const sending = {
      leaseId,
      outcome: outcome ?? 'na',
      value: value ?? outcome,
    };

    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  const landmarkAssets =
    landmarksWithAssets?.landmarkToAssets?.[
      landmarksWithAssets?.landmarks[0]?._id
    ];
  const frames = landmarkAssets?.frames?.sort(
    (a: any, b: any) => Number(a.idx) - Number(b.idx),
  );

  return (
    <div className="flex size-full flex-col items-center justify-center">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <div className="size-[70vmax] flex-col items-center justify-center">
          <div className="flex-col items-center justify-center overflow-auto">
            {frames && (
              <ImageCarousel
                images={frames.map((frame: any) => frame.url)}
                detections={frames.map((frame: any) => {
                  return landmarkAssets?.detectionsByFrame?.[frame.idx]?.[0]
                    .detectionInfo;
                })}
                startWithIndex="largestDetection"
              />
            )}
          </div>
          <div className="grid items-center justify-center overflow-auto text-center">
            {elementDescription && <Markdown text={elementDescription} />}
          </div>
          <div>
            <TrainerController
              containerRef={controllerRef}
              isDataReady={isDataReady}
              onSubmit={onSubmit}
              data={data}
              tool={tool}>
              <TrainerButtons />
              <TrainerSelect />
              <TrainerInputs />
            </TrainerController>
          </div>
        </div>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default HeightRestrictionType;
